import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Table, Pagination, Select } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatAuditTimestamp } from '@/utils/format-date'

type IntegrationErrorEntry = {
  id: string
  service: string
  operation: string
  httpStatus: number | null
  errorCode: string | null
  errorMessage: string
  context: Record<string, unknown> | null
  worker: string
  createdAt: string
}

const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'clerk', label: 'Clerk' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'sendgrid', label: 'SendGrid' },
  { value: 'graylog', label: 'Graylog' },
]

const WORKER_OPTIONS = [
  { value: '', label: 'All Workers' },
  { value: 'admin-api', label: 'Admin API' },
  { value: 'alert-worker', label: 'Alert Worker' },
  { value: 'billing-worker', label: 'Billing Worker' },
]

export function AdminIntegrationErrorsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const serviceParam = searchParams.get('service') ?? ''
  const workerParam = searchParams.get('worker') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [serviceFilter, setServiceFilter] = useState(serviceParam)
  const [workerFilter, setWorkerFilter] = useState(workerParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  const syncParams = useCallback(
    (params: Record<string, string>) => {
      const sp = new URLSearchParams()
      for (const [k, v] of Object.entries(params)) {
        if (v) sp.set(k, v)
      }
      setSearchParams(sp)
    },
    [setSearchParams]
  )

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const state: Record<string, string> = {
        service: serviceFilter,
        worker: workerFilter,
        page: '1',
        [key]: value,
      }
      setCurrentPage(1)
      syncParams(state)
    },
    [serviceFilter, workerFilter, syncParams]
  )

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-integration-errors', serviceFilter, workerFilter, offset],
    queryFn: () =>
      api.adminListIntegrationErrors({
        service: serviceFilter || undefined,
        worker: workerFilter || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    syncParams({
      service: serviceFilter,
      worker: workerFilter,
      page: String(page),
    })
  }

  const columns = [
    {
      key: 'createdAt',
      header: 'Timestamp',
      render: (e: IntegrationErrorEntry) => formatAuditTimestamp(e.createdAt),
    },
    {
      key: 'service',
      header: 'Service',
      render: (e: IntegrationErrorEntry) => (
        <ServiceBadge service={e.service} />
      ),
    },
    {
      key: 'operation',
      header: 'Operation',
      render: (e: IntegrationErrorEntry) => (
        <span className="font-mono text-xs">{e.operation}</span>
      ),
    },
    {
      key: 'httpStatus',
      header: 'Status',
      render: (e: IntegrationErrorEntry) => (
        <span className="font-mono text-xs">
          {e.httpStatus ?? '-'}
        </span>
      ),
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (e: IntegrationErrorEntry) => (
        <span className="text-sm text-gray-700 truncate max-w-xs block" title={e.errorMessage}>
          {e.errorCode ? `[${e.errorCode}] ` : ''}{e.errorMessage}
        </span>
      ),
    },
    {
      key: 'worker',
      header: 'Worker',
      render: (e: IntegrationErrorEntry) => (
        <span className="text-sm">{e.worker}</span>
      ),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Integration Errors</h1>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Select
            label="Service"
            name="service"
            value={serviceFilter}
            onChange={(e) => {
              setServiceFilter(e.target.value)
              updateFilter('service', e.target.value)
            }}
            options={SERVICE_OPTIONS}
            className="w-48"
          />
          <Select
            label="Worker"
            name="worker"
            value={workerFilter}
            onChange={(e) => {
              setWorkerFilter(e.target.value)
              updateFilter('worker', e.target.value)
            }}
            options={WORKER_OPTIONS}
            className="w-48"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load integration errors</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(e) => e.id}
              isLoading={isLoading}
              emptyMessage="No integration errors found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </Card>
    </div>
  )
}

function ServiceBadge({ service }: { service: string }) {
  const colors: Record<string, string> = {
    clerk: 'bg-purple-100 text-purple-800',
    stripe: 'bg-blue-100 text-blue-800',
    sendgrid: 'bg-green-100 text-green-800',
    graylog: 'bg-yellow-100 text-yellow-800',
  }
  const colorClass = colors[service] ?? 'bg-gray-100 text-gray-800'

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {service}
    </span>
  )
}

